import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.tsx';
import TopBar from './TopBar.tsx';
import FailOpenBanner from './FailOpenBanner.tsx';
import { usePageFocusMode } from '../../contexts/PageFocusContext.tsx';
import './AppLayout.css';

const PAGE_TITLES: Record<string, string> = {
  '/dashboard':  'Dashboard',
  '/inventory':  'Inventory',
  '/vendors':    'Vendors',
  '/orders':     'Orders',
  '/tasks':      'Tasks',
  '/schedule':   'Calendar',
  '/financials': 'Financials',
  '/settings':   'Settings',
  '/help':       'Help',
};

function getPageTitle(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith('/inventory/item/')) return 'Item Detail';
  return 'ProQure';
}

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { focusMode } = usePageFocusMode();

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(collapsed));
  }, [collapsed]);

  const pageTitle = getPageTitle(location.pathname);

  // Lock background scroll on mobile when sidebar overlay is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <div className="app-shell">
      <div className="blob-3" aria-hidden="true" />
      {/* Mobile sidebar backdrop — rendered when sidebar overlay is open */}
      {mobileOpen && !focusMode && (
        <div
          className="mobile-sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Focus mode (PageFocusContext) hides the sidebar/topbar chrome entirely — a page's own
          floating <PageFocusToggle /> is the only way back, and PageFocusProvider resets this
          on route change so navigating away always restores normal chrome. */}
      {!focusMode && (
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
      )}

      <div className={`main-content ${collapsed ? 'main-content-collapsed' : ''} ${focusMode ? 'main-content-focus' : ''}`}>
        {!focusMode && (
          <TopBar
            onMenuToggle={() => setCollapsed((c) => !c)}
            onMobileToggle={() => setMobileOpen((o) => !o)}
            pageTitle={pageTitle}
          />
        )}

        <main className={`page-content ${focusMode ? 'page-content-focus' : ''}`}>
          {/* Admin-only; renders nothing for every other role and nothing at all when the access
              gate is resolving normally. Inside <main> rather than above TopBar so it doesn't
              disturb the fixed topbar/mobile-toolbar offsets. */}
          <FailOpenBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
